import React from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router';

import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';
import {
  useGetExecutionLogsByGuidQuery,
  useGetTransformerLogsQuery,
} from '@/features/transformers/api/transformersApi';
import { LogViewer } from '@/features/transformers/components/LogViewer';

interface ExecutionLogPanelProps {
  tenantId: string;
}

function cleanGuid(guid: string): string {
  return guid.replace(/[\\/]+$/, '');
}

/**
 * Execution Log tab content -- shows the last recipe execution (limit=1, offset=0).
 * Separate from the "Recipe Running..." / "Results" tab (RunningStatusPanel).
 */
export const ExecutionLogPanel: React.FC<ExecutionLogPanelProps> = ({ tenantId }) => {
  const { recipeId, appId } = useParams<{ recipeId: string; appId: string }>();

  const { data: environments } = useGetEnvironmentsQuery(
    { tenantId, appId: appId! },
    { skip: !appId || !tenantId }
  );

  const {
    data: logs,
    isLoading,
    refetch: refetchLogs,
    isFetching: isFetchingLogs,
  } = useGetTransformerLogsQuery(
    {
      tenantId,
      transBaseId: recipeId || '',
      limit: 1,
      offset: 0,
    },
    { skip: !recipeId || !tenantId }
  );

  const latestLog = logs?.[0];

  const cleanedGuid = latestLog?.externalExecutionId
    ? cleanGuid(latestLog.externalExecutionId)
    : '';

  const {
    data: executionSteps,
    refetch: refetchSteps,
    isFetching: isFetchingSteps,
  } = useGetExecutionLogsByGuidQuery(
    { tenantId, executionGuid: cleanedGuid },
    { skip: !cleanedGuid }
  );

  const getEnvironment = (envId?: string) => {
    if (!envId || !environments) return null;
    return environments.find((e) => e.id === envId);
  };

  const handleRefresh = () => {
    refetchLogs();
    refetchSteps();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!latestLog) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No execution logs found
        </Typography>
      </Box>
    );
  }

  const env = getEnvironment(latestLog.environmentId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Execution summary header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f5f5f5',
          fontSize: 12,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
          Last Execution
        </Typography>

        {latestLog.startTime && (
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {format(new Date(latestLog.startTime), 'MMM do, h:mm:ss a')}
          </Typography>
        )}

        {env && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: env.color || '#404040',
                border: '1px solid black',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="body2" fontWeight={600} sx={{ fontSize: 12 }}>
              {env.name}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" sx={{ fontSize: 12 }}>
          v{latestLog.recipeVersionNumber || 1}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          color={latestLog.succeeded === true ? 'success.main' : 'error.main'}
          sx={{ fontSize: 12 }}
        >
          {latestLog.succeeded === true ? 'Success' : 'Failed'}
        </Typography>

        {latestLog.timer != null && (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {latestLog.timer.toFixed(2)}s
          </Typography>
        )}

        <Box sx={{ flex: 1 }} />

        <Link
          to={`/a/${appId}/health?recipe=${latestLog.baseId || recipeId}`}
          style={{ color: '#6C63FF', fontSize: 12, textDecoration: 'none' }}
        >
          View full history
        </Link>
      </Box>

      {/* Error display */}
      {latestLog.errorMessage && (
        <Box
          sx={{
            px: 1,
            py: 0.5,
            bgcolor: '#fde8e8',
            borderBottom: '1px solid #f5c6c6',
            flexShrink: 0,
          }}
        >
          <Typography variant="body2" color="error" sx={{ fontSize: 11, wordBreak: 'break-word' }}>
            {latestLog.errorMessage}
          </Typography>
        </Box>
      )}

      {/* Log viewer for steps */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <LogViewer
          steps={executionSteps}
          isRunning={false}
          onRefresh={handleRefresh}
          isFetching={isFetchingLogs || isFetchingSteps}
          compact
          appId={appId}
          environmentId={latestLog.environmentId}
        />
      </Box>
    </Box>
  );
};
